import { AnimateModal, ModalOpenProps } from '@/components/ui/modal';

import { useInsulationPaintingSetting } from '../hooks/useInsulationPaintingSetting';
import { ImageRaidoButton } from '../inputs';
import { TKEvent } from '../types';
import {
  INSULATION_AREA_OPTIONS,
  INSULATION_TYPE_OPTIONS,
  INSULATION_THICKNESS_OPTIONS,
  CLADDING_TYPE_OPTIONS,
  InsulationBrushSetting,
} from '../types/insulation';

/**
 * Insulation Painting Settings Modal
 * Lets the user pick serve category, insulation type, thickness, and cladding
 * type before painting insulation onto the drawing canvas with the brush.
 *
 * @log first created at 2026/08/14
 */
export const InsulationPaintingSettingModal = ({
  isOpen,
  isOnStage,
  lazyCloseHandler,
}: ModalOpenProps) => {
  const {
    serveCategory,
    exposeType,
    insulationType,
    thickness,
    claddingType,
    setExposeType,
    setInsulationType,
    setThickness,
    setCladdingType,
    categoryChangeHandler,
  } = useInsulationPaintingSetting();

  const startHandler = () => {
    const detail: InsulationBrushSetting = {
      serveCategory,
      exposeType,
      insulationType,
      insulationThickness: thickness,
      claddingType,
    };
    document.dispatchEvent(
      new CustomEvent(TKEvent.APPLY_INSULATION_PAINTING, { detail }),
    );
    lazyCloseHandler();
  };

  return (
    <AnimateModal
      isOpen={isOpen}
      isOnStage={isOnStage}
      lazyCloseHandler={lazyCloseHandler}
      title="Insulation Painting Settings"
      containerClass="w-auto max-h-full"
    >
      {/* Serve Category */}
      <h2 className="color-primary text-sm my-2">SERVE CATEGORY</h2>
      <div className="flex my-0 gap-6 w-80">
        <ImageRaidoButton
          label="DUCT"
          value="DUCT"
          name="serveCategory"
          selectedValue={serveCategory}
          selectChangeHandler={categoryChangeHandler}
        />
        <ImageRaidoButton
          label="PIPE"
          value="PIPE"
          name="serveCategory"
          selectedValue={serveCategory}
          selectChangeHandler={categoryChangeHandler}
        />
      </div>

      {/* Insulation Area - expose type */}
      <label className="color-primary text-sm block my-2">
        INSULATION AREA
        <select
          name="exposeType"
          className="block w-full px-2 py-1.5 mt-1 text-sm frame-input"
          value={exposeType}
          onChange={(event) => setExposeType(event.target.value)}
        >
          {INSULATION_AREA_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </label>

      {/* Insulation Type */}
      <label className="color-primary text-sm block my-2">
        INSULATION TYPE
        <select
          name="insulationType"
          className="block w-full px-2 py-1.5 mt-1 text-sm frame-input"
          value={insulationType}
          onChange={(event) => setInsulationType(event.target.value)}
        >
          {INSULATION_TYPE_OPTIONS[serveCategory].map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </label>

      {/* Thickness */}
      <label className="color-primary text-sm block my-2">
        THICKNESS
        <select
          name="thickness"
          disabled={insulationType === 'none'}
          className="block w-full px-2 py-1.5 mt-1 text-sm frame-input disabled:opacity-40 disabled:cursor-not-allowed"
          value={thickness}
          onChange={(event) => setThickness(event.target.value)}
        >
          {INSULATION_THICKNESS_OPTIONS[serveCategory].map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </label>

      {/* Cladding Type */}
      <label className="color-primary text-sm block my-2">
        CLADDING TYPE
        <select
          name="claddingType"
          disabled={insulationType === 'none'}
          className="block w-full px-2 py-1.5 mt-1 text-sm frame-input disabled:opacity-40 disabled:cursor-not-allowed"
          value={claddingType}
          onChange={(event) => setCladdingType(event.target.value)}
        >
          {CLADDING_TYPE_OPTIONS[serveCategory].map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </label>

      {/* Button Row */}
      <div className="flex gap-x-10 justify-between mx-0 mt-4">
        <button
          className="btn-red-border color-red font-semibold flex-1 red-shadow"
          onClick={() => lazyCloseHandler()}
        >
          Cancel
        </button>
        <button
          className="btn-green flex-1 leading-5 text-base shadow-md disabled:opacity-40 disabled:cursor-not-allowed"
          disabled={insulationType === 'none'}
          onClick={startHandler}
        >
          Start
        </button>
      </div>
    </AnimateModal>
  );
};
